// https://github.com/Willjfield/maplibre-gl-map-to-image/blob/main/index.js
// 一部修正して利用
import { domToPng, domToJpeg, domToSvg, domToCanvas } from "modern-screenshot";
import maplibregl from "maplibre-gl";
import { BASE_DPI } from "../../constants/Constant";

/**
 * Creates a raster image from a MapLibre map. The map is first captured as a virtual
 * clone, and then the image is generated from this clone. This process allows the
 * image to be rendered with all of the map's overlays and controls visible.
 * @param {object} map - The MapLibre map instance.
 * @param {object} options - Configuration options that dictate how the image should be generated.
 * @param {string} [options.targetImageId] - REQUIRED: The ID of the HTML element where the image should be inserted.
 * @param {boolean} [options.hideAllControls] - Flag to hide all map controls.
 * @param {array} [options.hideControlsInCorner] - Specific corners from which to hide controls.
 * @param {boolean} [options.hideMarkers] - Flag to hide all map markers.
 * @param {boolean} [options.hidePopups] - Flag to hide all map popups.
 * @param {array} [options.hideVisibleLayers] - Layer IDs to hide on the map by setting their visibility to 'none'.
 * @param {array} [options.showHiddenLayers] - Layer IDs to show on the map by setting their visibility to 'visible'.
 * @param {array} [options.bbox] - Optional bounding box to fit the map to, with padding.
 * @param {boolean} [options.coverEdits] - Flag to prevent seeing any map edits by capturing a background image before the map is rendered.
 * @param {string} [options.format] - The format of the generated image. Possible values are 'jpeg', 'png', 'svg', and 'canvas'.
 * @param {array} [options.center] - 追加：中心点
 * @param {array} [options.size] - 追加：画像サイズ
 * @param {number} [options.dpi] - 追加：画像のDPI
 * @returns {Promise} A promise that resolves when the image has been generated and inserted into the page.
 */
export async function toElement(map, options) {
  const mapElement = map.getContainer();
  const originalBounds = map.getBounds();
  const originalCenter = map.getCenter();

  const targetImageElement = document.getElementById(options.targetImageId);
  const mapZIndex = getComputedStyle(mapElement).zIndex;
  const mapHeight = mapElement.style.height;
  const mapWidth = mapElement.style.width;

  let photocover;
  const preserveDrawingBuffer = map?._preserveDrawingBuffer; // maplbre v4の修正

  const _coverEdits = options.coverEdits === false ? false : true;

  if (_coverEdits) {
    if (!preserveDrawingBuffer) {
      map._preserveDrawingBuffer = true;
      map.redraw();
      console.warn(
        "Setting the map's initial preserveDrawingBuffer setting to true may prevent screen flicker."
      );
    }

    const coverData = await domToPng(mapElement);
    photocover = document.createElement("img");
    mapElement.parentNode.prepend(photocover);
    photocover.style.height = `${mapElement.getBoundingClientRect().height}px`;
    photocover.style.width = `${mapElement.getBoundingClientRect().width}px`;
    photocover.style.position = "absolute";
    photocover.style.top = "0";
    photocover.style.left = "0";
    photocover.style.zIndex = mapZIndex + 1;
    photocover.setAttribute("id", "photocover");
    photocover.src = coverData;
  }

  if (typeof mapZIndex === "string") mapElement.style.zIndex = 0;

  // スケールの表示
  const scaleCtl = new maplibregl.ScaleControl({ unit: "metric" });
  map.addControl(scaleCtl, "bottom-left");
  // 出典の位置調整
  const brCtl = mapElement.querySelector(".maplibregl-ctrl-bottom-right");
  const isActive = brCtl.classList.contains("active");
  isActive && brCtl.classList.remove("active");

  const virtualClone = mapElement.cloneNode(true);
  virtualClone.style.zIndex = -9999;
  if (options.size) {
    mapElement.style.height = `${options.size[0]}px`;
    mapElement.style.width = `${options.size[1]}px`;
  } else {
    mapElement.style.height = `${mapElement.getBoundingClientRect().height}px`;
    mapElement.style.width = `${mapElement.getBoundingClientRect().width}px`;
  }
  virtualClone.style.height = mapElement.style.height;
  virtualClone.style.width = mapElement.style.width;

  virtualClone.setAttribute("id", "virtual-map-container");
  removeElements(virtualClone, "canvas.maplibregl-canvas");

  if (options.bbox) {
    map.fitBounds(options.bbox, { animate: false });
  }
  if (options.center) {
    map.setCenter(options.center);
  }
  map.redraw();
  return new Promise((resolve, reject) => {
    map.once("idle", async () => {
      manageOverlayVisibility(map, options, virtualClone);

      // レンダリングが安定するのを待つために、遅延を追加する(安全のため3秒)
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // 描画完了後の最新のCanvasを取得してコピーする
      const newCanvas = copyCanvas(map.getCanvas());
      virtualClone
        .getElementsByClassName("maplibregl-canvas-container")[0]
        .append(newCanvas);

      mapElement.append(virtualClone);

      const scale = (options.dpi || BASE_DPI) / BASE_DPI;
      const libOptions = { scale };

      let imgFunc;
      switch (options.format) {
        case "jpeg":
          imgFunc = domToJpeg;
          break;
        case "svg":
          imgFunc = domToSvg;
          break;
        case "canvas":
          imgFunc = domToCanvas;
          break;
        case "png":
        default:
          imgFunc = domToPng;
          break;
      }

      try {
        const dataUrl = await imgFunc(virtualClone, libOptions);
        targetImageElement.src = dataUrl;
        mapElement.style.zIndex = mapZIndex;
        mapElement.style.height = mapHeight;
        mapElement.style.width = mapWidth;
        options.bbox && map.fitBounds(originalBounds);
        options.center && map.setCenter(originalCenter);
        map.resize();
        // スケールの非表示
        map.removeControl(scaleCtl);
        // 出典の位置調整
        isActive && brCtl.classList.add("active");
        virtualClone.remove();
        _coverEdits && (map._preserveDrawingBuffer = preserveDrawingBuffer);
        if (photocover) {
          photocover.remove();
        }
        resolve();
      } catch (err) {
        console.error("ERROR: ", err);
        reject(err);
      }
    });
  });
}

/**
 * Manages the visibility of various overlay elements on a virtual map clone based on provided options.
 *
 * @param {object} map - The MapLibre map instance.
 * @param {object} options - Configuration options that dictate which elements to hide or show.
 * @param {boolean} [options.hideAllControls] - Flag to hide all map controls.
 * @param {array} [options.hideControlsInCorner] - Specific corners from which to hide controls.
 * @param {boolean} [options.hideMarkers] - Flag to hide all map markers.
 * @param {boolean} [options.hidePopups] - Flag to hide all map popups.
 * @param {array} [options.hideVisibleLayers] - Layer IDs to hide on the map by setting their visibility to 'none'.
 * @param {array} [options.showHiddenLayers] - Layer IDs to show on the map by setting their visibility to 'visible'.
 * @param {array} [options.bbox] - Optional bounding box to fit the map to, with padding.
 * @param {HTMLElement} virtualClone - The cloned map container where the visibility changes are applied.
 */

function manageOverlayVisibility(map, options, virtualClone) {
  if (options.hideAllControls) {
    removeElements(virtualClone, ".maplibregl-control-container");
  }

  if (options.hideControlsInCorner) {
    options.hideControlsInCorner.forEach((corner) => {
      removeElements(virtualClone, `.maplibregl-ctrl-${corner}`);
    });
  }

  if (options.hideMarkers) {
    removeElements(virtualClone, ".maplibregl-marker");
  }

  if (options.hidePopups) {
    removeElements(virtualClone, ".maplibregl-popup");
  }

  if (options.hideVisibleLayers) {
    options.hideVisibleLayers.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", "none");
      } else {
        console.warn(`layer ${layerId} not found`);
      }
    });
  }

  if (options.showHiddenLayers) {
    options.showHiddenLayers.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", "visible");
      } else {
        console.warn(`layer ${layerId} not found`);
      }
    });
  }

  if (options.bbox) {
    map.fitBounds(options.bbox, { padding: 20 });
  }
}

/**
 * Removes all DOM elements matching the specified selector within a given parent element.
 *
 * @param {HTMLElement} element - The parent element containing elements to be removed.
 * @param {string} selector - The CSS selector to match elements that should be removed.
 */

function removeElements(element, selector) {
  Array.from(element.querySelectorAll(selector)).forEach((el) => el.remove());
}

/**
 * Creates a new canvas element and copies the content of the given canvas element to it.
 *
 * @param {HTMLElement} canvas - The canvas element to be copied.
 * @returns {HTMLElement} A new canvas element with the same dimensions and content as the original.
 */
function copyCanvas(canvas) {
  const newCanvas = document.createElement("canvas");
  const rect = canvas.getBoundingClientRect();

  // canvasの実際の描画バッファのサイズ(高DPIを考慮)を使用する
  newCanvas.width = canvas.width;
  newCanvas.height = canvas.height;

  // スタイルはCSSピクセルサイズで指定する
  newCanvas.style.width = `${rect.width}px`;
  newCanvas.style.height = `${rect.height}px`;

  const context = newCanvas.getContext("2d");
  // canvas.width/height を使って描画することで、解像度を維持する
  context.drawImage(canvas, 0, 0, canvas.width, canvas.height);
  return newCanvas;
}
